import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import { entryFind } from '../api/endpoints/reports';
import type { ReservationReportRow } from '../api/types/reports';
import type { PostSaleResponse, ProcessHistoryEntry } from '../api/types/postSale';
import type { AirBooking } from '../api/types/air';
import type { ShoppingCartResponse } from '../api/types/shoppingCart';
import { cancelAirReservation } from '../api/endpoints/air';
import { cancelHotelReservation, getHotelReservationCancelPenalty } from '../api/endpoints/hotel';
import { HotelCancelPenaltyBreakdown } from '../modules/hotel/components/HotelCancelPenaltyBreakdown';
import { cancelRentCarOrder } from '../api/endpoints/rentCar';
import { getShoppingCart } from '../api/endpoints/shoppingCart';
import { isPaymentResumable, resumePaymentUrl } from '../modules/air/paymentResume';
import { AirBookingFlightDetails } from '../modules/air/components/AirBookingFlightDetails';
import { useAuthStore } from '../auth/authStore';
import { ApiErrorAlert } from './ApiErrorAlert';
import {
  AIR_CAPS,
  capabilitiesForKind,
  hasPostSaleActions,
  reservationRecordKind,
  type ReservationCapabilities,
} from './reservation/capabilities';
import { cancellationSummary, isCancelledStatus } from './reservation/cancellation';
import { mergeCartRows, reservationRowsFromCart } from './reservation/cartRows';
import { PostSaleActions } from './reservation/PostSaleActions';
import { RePriceAction } from './reservation/RePriceAction';
import { TicketPostSaleActions, type TicketRef } from './reservation/TicketPostSaleActions';
import { TicketImportSection } from './reservation/TicketImportSection';
import { DocumentMenu } from './reservation/DocumentMenu';
import { InsurancePolicyButton } from './reservation/InsurancePolicyButton';
import { ProcessHistoryPanel } from './reservation/ProcessHistoryPanel';

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2">{value === undefined || value === null || value === '' ? '—' : value}</Typography>
    </Grid>
  );
}

function ReservationRowCard({
  row,
  allowCancel,
  onCancel,
  documentActions,
  extraActions,
  insuranceTeklifId,
}: {
  row: ReservationReportRow;
  allowCancel: boolean;
  onCancel: () => void;
  /** Bu bilete özel "Belgeler" menüsü (e-bilet fiyatlı/fiyatsız). */
  documentActions?: React.ReactNode;
  /** Ticket-state actions (Void / İade) — mutually exclusive with İptal Et. */
  extraActions?: React.ReactNode;
  /** Sepette Tamamlıyo sigortası varsa `travelInsurances[0].id` (= teklifId) — PNR'ın
   *  yanında gösterilir. */
  insuranceTeklifId?: string;
}) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={2}>
          <Field label="PNR" value={row.pnr} />
          {insuranceTeklifId && <Field label="Sigorta Poliçe No" value={insuranceTeklifId} />}
          <Field label="Bilet No" value={row.documentNumber} />
          <Field label="Durum" value={row.statu} />
          <Field label="Sağlayıcı" value={row.provider} />
          <Field label="CRS" value={row.cRS} />
          <Field label="Yolcu" value={[row.firstName, row.lastName].filter(Boolean).join(' ')} />
          <Field label="Güzergah" value={[row.origin, row.destination].filter(Boolean).join(' → ')} />
          <Field label="Gidiş Tarihi" value={row.departureDate} />
          <Field label="Dönüş Tarihi" value={row.returnDate} />
          <Field label="Rezervasyon Tarihi" value={row.bookedAt || row.prebookedAt} />
          <Field label="Son Geçerlilik" value={row.expireAt} />
          <Field label="Toplam Ücret" value={row.totalFare?.toLocaleString('tr-TR')} />
          <Field label="Baz Ücret" value={row.baseFare?.toLocaleString('tr-TR')} />
          <Field label="Vergi" value={row.totalTax?.toLocaleString('tr-TR')} />
          <Field label="Ödeme Tipi" value={row.paymentTypes} />
          <Field label="Kullanıcı" value={row.username} />
          <Field label="ShoppingCartId" value={row.shoppingCartId} />
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? 'Ham Veriyi Gizle' : 'Ham Veriyi Göster'}
          </Button>
          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: 'auto', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            {documentActions}
            {allowCancel && row.shoppingCartId && (
              <Button size="small" color="error" startIcon={<CancelOutlinedIcon />} onClick={onCancel}>
                İptal Et
              </Button>
            )}
            {extraActions}
          </Stack>
        </Stack>
        {showRaw && (
          <Box
            component="pre"
            sx={{
              mt: 1,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              maxHeight: 320,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(row, null, 2)}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Shared reservation-detail page, reached by clicking a PNR/ticket number in any
 * reservation list (see modules/air/components/AirReservationList.tsx,
 * modules/air/pages/AirEntryFindPage.tsx, and the Hotel/Bus/Transfer/RentCar
 * `*ReservationsPage.tsx` files — none of them show inline row actions anymore).
 * Re-fetches the record fresh via `Entry/Find` (see api/endpoints/reports.ts) keyed off
 * the route's `:identifier` param + a `by=pnr|ticket` query param, rather than relying on
 * router state — this keeps the page deep-linkable and safe to reload directly.
 * `Entry/Find` is a generic cross-provider lookup (swagger tags it `Report`, not
 * Air-specific), so this same component backs every product module's detail route.
 *
 * The page has two states, chosen from the fetched record's status via
 * `reservationRecordKind` and applied by narrowing `capabilities` with
 * `capabilitiesForKind` (both in reservation/capabilities.ts):
 *  - **Rezervasyon** (every pax row still `Prebooked`): İptal Et (`capabilities.cancel`),
 *    Opsiyon/Fiyat and Ödemeye Git.
 *  - **Bilet** (any ticket issued — `Booked`/`Void`/`Refund`): per-ticket Void + İade
 *    (`capabilities.void` → `/Void`, `capabilities.refund` → `ShoppingCart/Refund`) on
 *    each row card via `TicketPostSaleActions`, and — for Air — never İptal Et.
 * `capabilities.cancel` is a product tag: `air` → `Air/CancelReservation` (Prebooked only),
 * `hotel` → `Hotel/GetReservationCancel`, `rentcar` → `RentCar/CancelOrder` (the last two
 * have no ticket concept, so İptal Et stays available regardless of status). Opsiyon/Fiyat,
 * Void/İade, resume-payment and flight-details are Air-only.
 * `airResumePayment` is the
 * same idea for jumping back into the Air booking flow's payment step (see
 * modules/air/paymentResume.ts) — only offered while the row's `expireAt` hasn't passed.
 * `showAirFlightDetails` re-fetches the full `ShoppingCart` (via `GetShoppingCart`, not
 * `Entry/Find`'s flat row) and renders `AirBookingFlightDetails` — the rich flight
 * segment/fare-breakdown view — also Air-only, since that component reads Air-specific
 * `AirBooking`/`FlightSegment` shapes.
 */
export function ReservationDetailPage({
  capabilities = AIR_CAPS,
}: {
  capabilities?: ReservationCapabilities;
}) {
  const [processHistories, setProcessHistories] = useState<ProcessHistoryEntry[] | null>(null);
  const { identifier } = useParams<{ identifier: string }>();
  const [searchParams] = useSearchParams();
  const by = searchParams.get('by') === 'ticket' ? 'ticket' : 'pnr';
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.currentUser);

  const [cancelRow, setCancelRow] = useState<ReservationReportRow | null>(null);
  const [locator, setLocator] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const queryKey = ['reservationDetail', by, identifier];
  const query = useQuery({
    queryKey,
    queryFn: () => entryFind(by === 'ticket' ? { ticket: identifier } : { pnr: identifier }),
    enabled: !!identifier,
  });

  const baseRows: ReservationReportRow[] = query.data?.reservationReports ?? [];

  // Sepet çekme kararı, birleştirilmiş `rows` değil taban sonuç üzerinden verilir
  // (döngü olmasın: `rows` sepete, sepet bu karara bağlı).
  const gateCaps = capabilitiesForKind(
    capabilities,
    reservationRecordKind(baseRows.map((r) => r.statu))
  );
  const shoppingCartId = baseRows[0]?.shoppingCartId;
  const cartQuery = useQuery({
    queryKey: ['reservationDetailCart', shoppingCartId],
    queryFn: () => getShoppingCart(shoppingCartId as string),
    enabled: (!!gateCaps.flightDetails || !!gateCaps.void || !!gateCaps.refund) && !!shoppingCartId,
  });

  /**
   * `Entry/Find` bir tek ticket/PNR ile çağrıldığında yalnız o kaydı döndürür. Çok noktalı
   * (multi-city) rezervasyonda her bacak ayrı `AirBooking` + ayrı PNR + ayrı bilet — eksik
   * bacakların bilet satırlarını, sayfaya ZATEN gelen `GetShoppingCart` yanıtından üretip
   * (ikinci bir API çağrısı yok) tıklanan bacağın tam satırıyla birleştir.
   */
  const rows: ReservationReportRow[] = mergeCartRows(
    baseRows,
    reservationRowsFromCart(cartQuery.data?.shoppingCart)
  );
  const primaryRow =
    rows.find((r) => r.documentNumber === identifier || r.pnr === identifier) ?? rows[0];
  const cancellation = cancellationSummary(rows);
  const refetch = () => queryClient.invalidateQueries({ queryKey });

  const recordKind = reservationRecordKind(rows.map((r) => r.statu));
  const caps = capabilitiesForKind(capabilities, recordKind);
  // Uçuşa iliştirilmiş Tamamlıyo sigorta poliçesi — sepet zaten `flightDetails` için çekiliyor.
  const insuranceTeklifId = (cartQuery.data?.shoppingCart?.travelInsurances?.[0]?.id as
    | string
    | undefined) || undefined;
  const cancelKind = caps.cancel;
  const allowCancel = !!cancelKind;
  const allowVoid = !!caps.void;
  const allowRefund = !!caps.refund;
  const airResumePayment = !!caps.resumePayment;
  const showAirFlightDetails = !!caps.flightDetails;

  // Biletlenmiş Air kaydında e-bilet her satıra (bilete) özel gösterilir; üst menüde
  // yalnız "Toplu" (tüm sepet) belgeleri kalır. Rezervasyon (Prebooked) aşamasında henüz
  // bilet yok → e-bilet üstte kalır.
  const perTicketDocuments = recordKind === 'ticket' && !!caps.documents?.includes('eticket');
  const topDocumentKinds = perTicketDocuments
    ? (caps.documents ?? []).filter((k) => k !== 'eticket')
    : caps.documents ?? [];

  /** Void / İade need the cart's `bookingId` + `paxId` GUIDs, absent from the flat
   *  `Entry/Find` row — match the row's ticket number against the cart's breakdowns. */
  const resolveTicketRef = (r: ReservationReportRow): TicketRef => {
    const cart = cartQuery.data?.shoppingCart;
    if (!cart) return {};
    for (const b of (cart.airBookings ?? []) as AirBooking[]) {
      for (const pb of b.passengerBreakdowns ?? []) {
        if (pb.ticketNumber && r.documentNumber && pb.ticketNumber === r.documentNumber) {
          return { bookingId: b.id, paxId: pb.paxReference?.paxReferanceId, breakdown: pb };
        }
      }
    }
    return {};
  };

  const captureHistories = (res: PostSaleResponse | ShoppingCartResponse) => {
    const histories = (res.shoppingCart as { processHistories?: ProcessHistoryEntry[] | null } | null | undefined)
      ?.processHistories;
    if (histories && histories.length > 0) setProcessHistories(histories);
    refetch();
  };

  // Otel iptalinde `Hotel/GetReservationCancel`'den ÖNCE ceza kırılımını çek — iptal
  // diyaloğu açıkken. Hata verirse (`retry: false`) diyalog kırılım olmadan + küçük bir
  // "ceza bilgisi alınamadı" notuyla gösterilir; iptal akışı bloke olmaz.
  const hotelCancelId = cancelRow?.pnr || cancelRow?.documentNumber || '';
  const hotelCancelPenaltyQuery = useQuery({
    queryKey: ['hotelCancelPenalty', hotelCancelId],
    // Taahhüt edilmiş rezervasyon PNR ile sorgulanır (shoppingCartId gönderilmez → sunucu
    // `GetFromShoppingCart:false` yolunu kullanır).
    queryFn: () => getHotelReservationCancelPenalty(hotelCancelId),
    enabled: !!cancelRow && cancelKind === 'hotel' && !!hotelCancelId,
    retry: false,
    staleTime: 30 * 1000,
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      const shoppingCartId = cancelRow?.shoppingCartId ?? '';
      if (cancelKind === 'rentcar') {
        return cancelRentCarOrder({ shoppingCartId });
      }
      if (cancelKind === 'hotel') {
        // `Hotel/GetReservationCancel` wants a `reservationNumber` (+ required `reason`).
        // The flat `Entry/Find` row doesn't label it — pnr is the likeliest carrier, with
        // documentNumber as a fallback. Verify against live data (docs/backend-requirements.md).
        return cancelHotelReservation(cancelRow?.pnr || cancelRow?.documentNumber || '', cancelReason.trim());
      }
      return cancelAirReservation({
        shoppingCartId,
        bookingData: { locator: locator || cancelRow?.pnr || '' },
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Rezervasyon iptal edildi.', { variant: 'success' });
      setCancelRow(null);
      refetch();
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İptal başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} aria-label="Geri">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {recordKind === 'ticket' ? 'Bilet Detayı' : 'Rezervasyon Detayı'} — {identifier}
        </Typography>
        {query.isFetching && <CircularProgress size={20} />}
        <Stack direction="row" spacing={1} sx={{ ml: 'auto', alignItems: 'center' }}>
          {/* SA-3: "Yeniden Fiyatla" (`Air/RePrice`) — still-held (Prebooked) Air reservation only. */}
          {caps.reprice && primaryRow && (
            <RePriceAction row={primaryRow} cart={cartQuery.data?.shoppingCart} />
          )}
          {airResumePayment && primaryRow && isPaymentResumable(primaryRow) && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentOutlinedIcon />}
              onClick={() => navigate(resumePaymentUrl(primaryRow))}
            >
              Ödemeye Git
            </Button>
          )}
        </Stack>
      </Stack>

      {cancellation && (
        <Alert
          severity="error"
          variant="filled"
          icon={<CancelOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2, fontWeight: 600, alignItems: 'center' }}
        >
          {cancellation.scope === 'full'
            ? 'Bu rezervasyon iptal edilmiş.'
            : 'Bu rezervasyonda iptal / iade edilmiş bilet var.'}
          {cancellation.statuses.length > 0 && (
            <Typography component="span" sx={{ ml: 1, fontWeight: 400, opacity: 0.9 }}>
              ({cancellation.statuses.join(', ')})
            </Typography>
          )}
        </Alert>
      )}

      <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />

      {!query.isFetching && query.isSuccess && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          "{identifier}" için kayıt bulunamadı.
        </Typography>
      )}

      {showAirFlightDetails && cartQuery.isFetching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {showAirFlightDetails && cartQuery.data?.shoppingCart && (
        <Box sx={{ mb: 3 }}>
          <AirBookingFlightDetails
            shoppingCart={cartQuery.data.shoppingCart}
            isCorporate={currentUser?.gsaType === 'CORPORATE'}
          />
        </Box>
      )}

      {primaryRow &&
        (topDocumentKinds.length > 0 || hasPostSaleActions(caps) || !!insuranceTeklifId) && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {topDocumentKinds.length > 0 && primaryRow.shoppingCartId && (
              <DocumentMenu
                shoppingCartId={primaryRow.shoppingCartId}
                kinds={topDocumentKinds}
                pnr={perTicketDocuments ? undefined : primaryRow.pnr}
                cart={cartQuery.data?.shoppingCart}
                label={perTicketDocuments ? 'Toplu Belge' : 'Belgeler'}
              />
            )}
            {insuranceTeklifId && <InsurancePolicyButton teklifId={insuranceTeklifId} />}
            <PostSaleActions row={primaryRow} caps={caps} onDone={captureHistories} />
          </Stack>
        )}

      <Stack spacing={2}>
        {rows.map((row, index) => (
          <ReservationRowCard
            key={(row.shoppingCartId ?? 'row') + index}
            row={row}
            insuranceTeklifId={insuranceTeklifId}
            allowCancel={allowCancel}
            onCancel={() => {
              setLocator('');
              setCancelReason('');
              setCancelRow(row);
            }}
            documentActions={
              perTicketDocuments && row.shoppingCartId && (row.pnr || row.documentNumber) ? (
                <DocumentMenu
                  shoppingCartId={row.shoppingCartId}
                  kinds={['eticket']}
                  pnr={row.pnr}
                  paxReferenceId={resolveTicketRef(row).paxId}
                  cart={cartQuery.data?.shoppingCart}
                  label="Bilet Belgesi"
                />
              ) : undefined
            }
            extraActions={
              recordKind === 'ticket' && (allowVoid || allowRefund) && !isCancelledStatus(row.statu) ? (
                <TicketPostSaleActions
                  row={row}
                  ticketRef={resolveTicketRef(row)}
                  allowVoid={allowVoid}
                  allowRefund={allowRefund}
                  cartPending={cartQuery.isFetching}
                  onDone={captureHistories}
                />
              ) : undefined
            }
          />
        ))}
      </Stack>

      <ProcessHistoryPanel
        entries={
          processHistories ??
          ((cartQuery.data?.shoppingCart?.processHistories as ProcessHistoryEntry[] | undefined) ?? null)
        }
      />

      {/* SA-3: Arkman ticket import — pinned to the bottom of every Air ticket-detail page. */}
      {recordKind === 'ticket' && showAirFlightDetails && cartQuery.data?.shoppingCart && (
        <TicketImportSection
          shoppingCartId={shoppingCartId ?? ''}
          airBookings={(cartQuery.data.shoppingCart.airBookings ?? []) as AirBooking[]}
          passengers={cartQuery.data.shoppingCart.passengers}
        />
      )}

      {allowCancel && (
        <Dialog
          open={!!cancelRow}
          onClose={() => !cancelMutation.isPending && setCancelRow(null)}
          maxWidth={cancelKind === 'hotel' ? 'sm' : 'xs'}
          fullWidth
        >
          <DialogTitle>Rezervasyonu İptal Et</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {cancelKind === 'air' && (
                <TextField
                  key={cancelRow?.shoppingCartId ?? cancelRow?.pnr}
                  label="PNR / Locator"
                  size="small"
                  fullWidth
                  defaultValue={cancelRow?.pnr ?? ''}
                  onChange={(e) => setLocator(e.target.value)}
                  helperText="Boşsa yukarıdaki PNR değeri kullanılır."
                />
              )}
              {cancelKind === 'hotel' && (
                <>
                  {hotelCancelPenaltyQuery.isFetching && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  {hotelCancelPenaltyQuery.isError && (
                    <Alert severity="warning">Ceza bilgisi alınamadı.</Alert>
                  )}
                  {hotelCancelPenaltyQuery.data && (
                    <HotelCancelPenaltyBreakdown body={hotelCancelPenaltyQuery.data.body} />
                  )}
                  <TextField
                    key={cancelRow?.shoppingCartId ?? cancelRow?.pnr}
                    label="İptal nedeni"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                Bu işlem geri alınamaz.
              </Typography>
              <ApiErrorAlert error={cancelMutation.error} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelRow(null)} disabled={cancelMutation.isPending}>
              Vazgeç
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={cancelMutation.isPending || (cancelKind === 'hotel' && !cancelReason.trim())}
              onClick={() => cancelMutation.mutate()}
            >
              {cancelMutation.isPending ? 'İptal ediliyor…' : 'İptal Et'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
